/**
 * The portfolio picker (moved out of PortfolioViewTab, Pedro 2026-10-23: "can we have the
 * rebalance to... portfolio picker have the same look and feel and the portfolio picker used for
 * View Portfolio and Trade Portfolio?"). One look for every place a stored portfolio is chosen:
 * a button that opens a search box, the list of portfolios (each naming its agreement), and an
 * apply button + Cancel.
 *
 * Multi (View portfolio, Trades): HP-C3 (Pedro 2026-10-20) a dropdown of ticks that collapses to
 * one line — the portfolio's label, or "N portfolios" (the summary line under the toolbar names
 * them). Since 0113 the values are refs (bank + number) and the labels "BNP · 000123456".
 * PICK-SHOW (Pedro 2026-10-22: "accept the ticks and wait for the user to click a "Show"
 * button"): ticks are a draft; **Show** applies them, **Cancel** (or a click outside) drops
 * them — the draft-until-OK shape of the column filters (C-293).
 *
 * Single (`single`, Trades → Rebalance to…): one portfolio, chosen with a radio; the button is
 * the caller's, and the apply button names the action.
 *
 * TRADE-CONS U4 step 2 (Pedro 2026-10-29: "In View mode the picker can pick Portfolios or
 * Consolidations, but the app refuses loudly to simultaneously show anything that overlaps …
 * explains the duplication and asks to "deselect the other before including this one""): in multi
 * mode a Consolidations section lists the consolidations (on an agreement's tab, that agreement's);
 * ticking one shows all its portfolios. Ticking a consolidation with a portfolio already ticked (on
 * its own or through another consolidation), or a portfolio of a ticked consolidation, is refused
 * in red, naming both. `onChange` also returns the ticked consolidations.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Anchor, Button, Checkbox, Divider, Group, Popover, Radio, ScrollArea, Stack, Text, TextInput, Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertTriangle, IconChevronDown, IconStack2 } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { portfolioConsolidationsApi, type Consolidation } from '../api/portfolioConsolidations';
import { portfoliosApi, splitRef } from '../api/portfolios';

/** PF-MAX: the picker's "All portfolios" (the server's `portfolios=all`: the whole book on a date). */
export const ALL_PORTFOLIOS = 'all';

export function PortfolioPicker({ value, onChange, only, single, consolidations = [] }: {
  value: string[];
  /** `cons`: the consolidations ticked (U4 step 2); `v` includes all their portfolios. */
  onChange: (v: string[], cons?: string[]) => void;
  /** the consolidations currently shown (their ids) */
  consolidations?: string[];
  /** Step 4 (P4): the Agreement → Portfolios tab offers only that agreement's portfolios. */
  only?: { ref: string; label: string }[];
  /** One portfolio (Rebalance to…): the button's text and tooltip, a line above the list, the
   *  apply button's text, the portfolio left out (the one being traded), and a busy flag. */
  single?: { button: string; tip?: string; intro?: string; action: string; exclude?: string | null; busy?: boolean;
    /** M-REB (Pedro 2026-10-26: Rebalance to… opens a choice, Model or Portfolio, "which then open
     *  either a Model picker or a Portfolio picker"): opened by the caller, anchored to `target`. */
    opened?: boolean; onClose?: () => void; target?: React.ReactNode };
}) {
  const controlled = single?.opened !== undefined;
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlled ? !!single?.opened : ownOpen;
  const setOpen = (v: boolean | ((o: boolean) => boolean)) => {
    const next = typeof v === 'function' ? v(open) : v;
    if (controlled) { if (!next) single?.onClose?.(); } else setOwnOpen(next);
  };
  // The consolidations (U4 step 2): read with portfolios.read; offered in multi mode only.
  const consQ = useQuery({ queryKey: ['portfolio-consolidations'], queryFn: () => portfolioConsolidationsApi.list(),
    enabled: !single });
  const allCons: Consolidation[] = consQ.data?.items ?? [];
  const consById = useMemo(() => Object.fromEntries(allCons.map((c) => [c.id, c])), [allCons]);
  const membersOf = (id: string) => (consById[id]?.members ?? []).map((m) => m.ref);
  const [draftCons, setDraftCons] = useState<string[]>(consolidations);
  const [refusal, setRefusal] = useState<string | null>(null);
  // `draft` holds the portfolios ticked on their own; a ticked consolidation's portfolios are added on Show.
  const ownOf = (refs: string[], cons: string[]) => {
    const inCons = new Set(cons.flatMap(membersOf));
    return refs.filter((r) => !inCons.has(r));
  };
  const [draft, setDraft] = useState<string[]>(value);
  // Opening starts from what is shown; closing without Show drops the draft.
  useEffect(() => {
    if (open) { setDraftCons(consolidations); setDraft(ownOf(value, consolidations)); setRefusal(null); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 250);
  const res = useQuery({ queryKey: ['portfolios', 'search', q], queryFn: () => portfoliosApi.search(q),
    enabled: open && !only });
  // Labels seen so far, so a ticked portfolio keeps its label when a new search hides it.
  const [seen, setSeen] = useState<Record<string, string>>({});
  useEffect(() => {
    const found = res.data?.items ?? [];
    if (found.length) setSeen((s) => ({ ...s, ...Object.fromEntries(found.map((i) => [i.id, i.label])) }));
  }, [res.data]);
  const onlyLabels = useMemo(() => Object.fromEntries((only ?? []).map((o) => [o.ref, o.label])), [only]);
  const labelOf = (r: string) => onlyLabels[r] ?? seen[r] ?? splitRef(r).number;
  // PF-MAX (Pedro 2026-10-22: "see our consolidated portfolio in the same way as I can see each
  // individual portfolio"): "All portfolios" = the whole book on the view's date, asked of the
  // server as `all` (the search lists 25 at most, so ticking could never reach every one). On an
  // agreement's tab it ticks that agreement's portfolios. It replaces PICK-ALL's "Select all".
  // Not offered when choosing one portfolio.
  const allQ = useQuery({ queryKey: ['portfolios', 'search', ''], queryFn: () => portfoliosApi.search(''),
    enabled: open && !only && !single });
  const allCount = single ? 0 : only ? only.length : allQ.data?.total;
  const isAll = draft.length === 1 && draft[0] === ALL_PORTFOLIOS;
  const onlyAll = !!only && only.length > 0 && only.every((o) => draft.includes(o.ref));
  const toggleAll = () => {
    setRefusal(null);
    setDraftCons([]);   // "All" covers every portfolio: a consolidation on top would show them twice
    if (only) setDraft(onlyAll ? [] : only.map((o) => o.ref));
    else setDraft(isAll ? [] : [ALL_PORTFOLIOS]);
  };
  const items = useMemo(() => {
    // PICK-SEL (Pedro 2026-10-29): the list keeps its order; ticked items stay in place (the
    // Selected section at the top lists them, whatever the search).
    const m = new Map<string, string>();
    if (single && draft[0] && draft[0] !== ALL_PORTFOLIOS) m.set(draft[0], labelOf(draft[0]));   // the one chosen, kept visible
    if (only) {
      const t = search.trim().toLowerCase();
      only.filter((o) => !t || o.label.toLowerCase().includes(t)).forEach((o) => m.set(o.ref, o.label));
    } else {
      // Step 4: each line names its agreement (found by its code or mnemonic too).
      (res.data?.items ?? []).forEach((i) => m.set(i.id, i.agreement_label ? `${i.label} · ${i.agreement_label}` : i.label));
    }
    if (single?.exclude) m.delete(single.exclude);
    return [...m];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, res.data, seen, only, search, single?.exclude]);
  const DESELECT = 'Deselect the other before including this one.';
  const toggle = (n: string) => {
    setRefusal(null);
    if (single) { setDraft([n]); return; }
    if (draft.includes(n)) { setDraft((d) => d.filter((x) => x !== n)); return; }
    const inside = draftCons.find((c) => membersOf(c).includes(n));
    if (inside) {
      setRefusal(`${labelOf(n)} is already shown as part of consolidation ${consById[inside]?.name}: it would be shown twice. ${DESELECT}`);
      return;
    }
    setDraft((d) => [...d, n]);
  };
  const toggleCons = (id: string) => {
    setRefusal(null);
    if (draftCons.includes(id)) { setDraftCons((c) => c.filter((x) => x !== id)); return; }
    const c = consById[id];
    for (const ref of membersOf(id)) {
      const label = c.members.find((m) => m.ref === ref)?.label ?? labelOf(ref);
      if (draft.includes(ref)) {
        setRefusal(`Consolidation ${c.name} includes ${label}, which is already ticked on its own: it would be shown twice. ${DESELECT}`);
        return;
      }
      const other = draftCons.find((o) => membersOf(o).includes(ref));
      if (other) {
        setRefusal(`Consolidation ${c.name} includes ${label}, which is already shown in consolidation ${consById[other]?.name}. ${DESELECT}`);
        return;
      }
    }
    setDraftCons((cs) => [...cs, id]);
  };
  const expanded = (refs: string[], cons: string[]) => [...new Set([...cons.flatMap(membersOf), ...refs])];
  const finalRefs = expanded(draft, draftCons);
  const changed = finalRefs.length !== value.length || finalRefs.some((r) => !value.includes(r))
    || draftCons.length !== consolidations.length || draftCons.some((c) => !consolidations.includes(c));
  const show = () => { onChange(finalRefs, draftCons); setOpen(false); };
  const valueIsAll = value.length === 1 && value[0] === ALL_PORTFOLIOS;
  const onlyOneCons = consolidations.length === 1 && consById[consolidations[0]]
    && value.length === membersOf(consolidations[0]).length;
  const summary = value.length === 0 ? 'Choose portfolios' : valueIsAll ? 'All portfolios'
    : onlyOneCons ? `Consolidation ${consById[consolidations[0]].name}`
    : value.length === 1 ? labelOf(value[0]) : `${value.length} portfolios`;
  // Consolidations offered: matching the search (name or agreement); on an agreement's tab, its own.
  const onlyRefs = useMemo(() => new Set((only ?? []).map((o) => o.ref)), [only]);
  const consItems = single ? [] : allCons.filter((c) => {
    if (only && !c.members.every((m) => onlyRefs.has(m.ref))) return false;
    const t = search.trim().toLowerCase();
    return !t || c.name.toLowerCase().includes(t) || c.agreement_label.toLowerCase().includes(t)
      || draftCons.includes(c.id);
  });
  const target = single?.target ? single.target : single ? (
    <Tooltip withArrow multiline w={320} label={single.tip} disabled={!single.tip || open}>
      <Button size="xs" variant="default" loading={single.busy} onClick={() => setOpen((o) => !o)}>{single.button}</Button>
    </Tooltip>
  ) : (
    <Button size="xs" variant="default" w={220} justify="space-between" rightSection={<IconChevronDown size={14} />}
      onClick={() => setOpen((o) => !o)} styles={{ label: { fontWeight: value.length ? 500 : 400 } }}>
      {summary}
    </Button>
  );
  const popover = (
    <Popover width={320} position="bottom-start" shadow="md" withArrow opened={open} onChange={setOpen} trapFocus>
      <Popover.Target>{target}</Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          {single?.intro && <Text size="xs" c="dimmed">{single.intro}</Text>}
          <TextInput size="xs" placeholder="Portfolio number, bank, or agreement (CIIF-301 / mnemonic)" value={search}
            onChange={(e) => setSearch(e.currentTarget.value)} />
          <ScrollArea.Autosize mah={300}>
            <Stack gap={4}>
              {/* PICK-SEL: what is ticked, at the top; unticking here unticks it below too. */}
              {!single && (isAll || draftCons.length > 0 || draft.length > 0) && (
                <>
                  <Text size="xs" fw={600}>Selected</Text>
                  {isAll ? (
                    <Checkbox size="xs" label={`All portfolios (${allCount ?? '…'})`} checked onChange={toggleAll} />
                  ) : (
                    <>
                      {draftCons.map((id) => (
                        <Checkbox key={`sel-c-${id}`} size="xs" checked onChange={() => toggleCons(id)}
                          label={<Group gap={4} wrap="nowrap"><IconStack2 size={12} />{consById[id] ? `${consById[id].name} · ${consById[id].agreement_label}` : '…'}</Group>} />
                      ))}
                      {draft.map((r) => <Checkbox key={`sel-p-${r}`} size="xs" checked onChange={() => toggle(r)} label={labelOf(r)} />)}
                    </>
                  )}
                  <Divider my={2} />
                </>
              )}
              {(allCount ?? 0) > 0 && (
                <Tooltip withArrow multiline w={280} label={only
                  ? "Tick every portfolio of this agreement"
                  : "The whole book: every portfolio with positions on the view's date, consolidated. Above 20 portfolios the table shows the Total columns only."}>
                  <Checkbox size="xs" fw={600} label={`All portfolios (${allCount})`}
                    checked={only ? onlyAll : isAll} onChange={toggleAll} />
                </Tooltip>
              )}
              {(allCount ?? 0) > 0 && <Divider my={2} />}
              {consItems.length > 0 && (
                <>
                  <Group gap={4}><IconStack2 size={13} /><Text size="xs" fw={600}>Consolidations</Text></Group>
                  {consItems.map((c) => (
                    <Tooltip key={c.id} withArrow multiline w={300} label={`${c.members.map((m) => m.label).join(' · ')}${c.model_name ? ` — model ${c.model_name}` : ''}`}>
                      <Checkbox size="xs" label={`${c.name} · ${c.agreement_label} (${c.members.length} portfolios)`}
                        checked={draftCons.includes(c.id)} disabled={isAll} onChange={() => toggleCons(c.id)} />
                    </Tooltip>
                  ))}
                  <Divider my={2} label="Portfolios" labelPosition="left" />
                </>
              )}
              {items.length === 0 && <Text size="xs" c="dimmed">{res.isLoading ? 'Searching…' : single ? 'No other stored portfolio' : 'No stored portfolio'}</Text>}
              {items.map(([n, label]) => (single
                ? <Radio key={n} size="xs" label={label} checked={draft[0] === n} onChange={() => toggle(n)} />
                : <Checkbox key={n} size="xs" label={label} checked={isAll || draft.includes(n)}
                    disabled={isAll} onChange={() => toggle(n)} />))}
            </Stack>
          </ScrollArea.Autosize>
          {refusal && (
            <Alert color="red" variant="filled" p="xs" icon={<IconAlertTriangle size={16} />} title="Shown twice"
              styles={{ body: { minWidth: 0 }, message: { overflowWrap: 'anywhere' } }}>
              <Text size="xs">{refusal}</Text>
            </Alert>
          )}
          <Group justify="space-between">
            <Text size="xs" c="dimmed">{single ? (draft[0] ? labelOf(draft[0]) : 'Choose one') : isAll ? 'All portfolios: untick it to choose some'
              : `${finalRefs.length} portfolio${finalRefs.length === 1 ? '' : 's'} chosen${draftCons.length ? ` (${draftCons.length} consolidation${draftCons.length === 1 ? '' : 's'})` : ''}`}</Text>
            {(draft.length > 0 || draftCons.length > 0) && <Anchor size="xs" onClick={() => { setDraft([]); setDraftCons([]); setRefusal(null); }}>Clear</Anchor>}
          </Group>
          <Group gap="xs" justify="flex-end">
            <Button size="xs" onClick={show} disabled={single ? !draft[0] || !!single.busy : !changed}>{single ? single.action : 'Show'}</Button>
            <Button size="xs" variant="default" onClick={() => setOpen(false)}>Cancel</Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
  if (single) return popover;
  return (
    <Stack gap={2}>
      <Text size="xs" fw={500}>Portfolios</Text>
      {popover}
    </Stack>
  );
}
